import React, { useState, useEffect } from 'react';
import { FileText, Save, Loader2, Clock, AlertTriangle, IndianRupee, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WillParameters = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parameters, setParameters] = useState({
    will_access_duration_days: 365, // Default 1 year
    partner_onboarding_fee: 1999, // Default onboarding fee
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchParameters = async () => {
    try {
      const response = await axios.get(`${API}/admin/will-parameters`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setParameters({
          will_access_duration_days: response.data.will_access_duration_days || 365,
          partner_onboarding_fee: response.data.partner_onboarding_fee ?? 1999,
        });
      }
    } catch (error) {
      console.error('Error fetching will parameters:', error);
      // Use defaults if not set
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (parameters.will_access_duration_days < 1) {
      toast.error('Access duration must be at least 1 day');
      return;
    }
    if (parameters.partner_onboarding_fee < 0) {
      toast.error('Onboarding fee cannot be negative');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/will-parameters`, parameters, {
        headers: getAuthHeader()
      });
      toast.success('Parameters saved successfully');
    } catch (error) {
      console.error('Error saving parameters:', error);
      toast.error('Failed to save parameters');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="will-parameters-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="w-7 h-7 text-amber-400" />
            Platform Parameters
          </h1>
          <p className="text-slate-400 mt-1">Configure global will access and partner settings</p>
        </div>
      </div>

      {/* Partner Onboarding Fee Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-400" />
            Partner Onboarding Fee
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <IndianRupee className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-300">
                <p className="font-medium mb-1">How this works:</p>
                <ul className="list-disc list-inside space-y-1 text-emerald-300/80">
                  <li>Partners must pay this one-time fee before they can add clients</li>
                  <li>Partners can login and view dashboard, but client/brokerage features are locked</li>
                  <li>Invoice is automatically generated and sent after payment</li>
                  <li>Set to 0 to disable onboarding fee (not recommended)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid gap-6 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="onboarding-fee" className="text-slate-300 font-medium">
                Onboarding Fee Amount (₹)
              </Label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                  <Input
                    id="onboarding-fee"
                    type="number"
                    min="0"
                    max="99999"
                    value={parameters.partner_onboarding_fee}
                    onChange={(e) => setParameters({
                      ...parameters,
                      partner_onboarding_fee: parseFloat(e.target.value) || 0
                    })}
                    className="bg-slate-800 border-slate-700 text-white w-40 pl-8"
                    data-testid="onboarding-fee-input"
                  />
                </div>
              </div>
              <p className="text-slate-500 text-sm">
                One-time payment required from new partners
              </p>
            </div>

            {/* Quick presets */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Quick presets:</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Free', value: 0 },
                  { label: '₹999', value: 999 },
                  { label: '₹1,999', value: 1999 },
                  { label: '₹2,999', value: 2999 },
                  { label: '₹4,999', value: 4999 },
                  { label: '₹9,999', value: 9999 },
                ].map((preset) => (
                  <Button
                    key={preset.value}
                    variant="outline"
                    size="sm"
                    onClick={() => setParameters({
                      ...parameters,
                      partner_onboarding_fee: preset.value
                    })}
                    className={`border-slate-700 ${
                      parameters.partner_onboarding_fee === preset.value
                        ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Will Access Duration Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Will Access Duration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-300">
                <p className="font-medium mb-1">How this works:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-300/80">
                  <li>When a client purchases a will, they get access for the duration specified below</li>
                  <li>After expiry, clients must pay a renewal fee (set by their partner) to continue editing</li>
                  <li>Clients can still view/download their completed will after expiry</li>
                  <li>This setting applies to all new will purchases</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid gap-6 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="duration" className="text-slate-300 font-medium">
                Access Duration (in days)
              </Label>
              <div className="flex items-center gap-4">
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  max="3650"
                  value={parameters.will_access_duration_days}
                  onChange={(e) => setParameters({
                    ...parameters,
                    will_access_duration_days: parseInt(e.target.value) || 1
                  })}
                  className="bg-slate-800 border-slate-700 text-white w-32"
                  data-testid="will-duration-input"
                />
                <span className="text-slate-400">days</span>
              </div>
              <p className="text-slate-500 text-sm">
                {parameters.will_access_duration_days >= 365 
                  ? `≈ ${(parameters.will_access_duration_days / 365).toFixed(1)} year(s)`
                  : parameters.will_access_duration_days >= 30
                    ? `≈ ${Math.round(parameters.will_access_duration_days / 30)} month(s)`
                    : `${parameters.will_access_duration_days} day(s)`
                }
              </p>
            </div>

            {/* Quick presets */}
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Quick presets:</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: '30 Days', value: 30 },
                  { label: '90 Days', value: 90 },
                  { label: '6 Months', value: 180 },
                  { label: '1 Year', value: 365 },
                  { label: '2 Years', value: 730 },
                  { label: 'Lifetime', value: 3650 },
                ].map((preset) => (
                  <Button
                    key={preset.value}
                    variant="outline"
                    size="sm"
                    onClick={() => setParameters({
                      ...parameters,
                      will_access_duration_days: preset.value
                    })}
                    className={`border-slate-700 ${
                      parameters.will_access_duration_days === preset.value
                        ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
          data-testid="save-parameters-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save All Parameters
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default WillParameters;
